import React, { useState } from 'react';
import AdminContainer from '../../../../Utility/AdminContainer';
import AdminBox from '../../../../Utility/AdminBox';
import AdminBoxHeadline from '../../../../Utility/AdminBoxHeadline';
import useGlobals from '../../../../../Hooks/useGlobals';
import useOpenApiConfig from '../../../../../Hooks/useOpenApiConfig';
import {
    BasicInformationSection,
    AuthenticationSection,
    OpenApiSpecSection,
    FormControls,
} from '../OpenApiTab/components';
import InfoButton from '../../../../Utility/InfoButton';
import OpenApiTester from './OpenApiTester';

function OpenApiConfigPage() {
    const { i18n } = useGlobals();
    const [resetFlag, setResetFlag] = useState(false);
    const {
        formData,
        errors,
        isLoading,
        config,
        isNew,
        handleSave,
        handleCancel,
        updateFormData,
        updateAuthData,
        activeTab,
        setActiveTab,
        isMobile,
        boxHeightClassesStr,
        configIdentifier,
    } = useOpenApiConfig({
        onSaved: () => setResetFlag((prev) => !prev),
    });

    return (
        <AdminContainer classList="!p-0 xl:!py-0">
            <div className={`h-[calc(var(--app-height)-70px)] xl:h-[calc(var(--app-height)-100px)] flex flex-col `}>
                <div className="block lg:hidden w-full px-6 pt-6">
                    <div className="flex w-full relative">
                        <div
                            style={{ left: `${(100 / 2) * activeTab}%`, width: `${100 / 2}%` }}
                            className="absolute z-[5] bottom-0 h-[2px] bg-primary transition-all duration-300 ease-in-out"
                        ></div>
                        <div className="absolute bottom-0 w-full h-[2px] bg-layout-borderColor transition-all duration-300 ease-in-out"></div>
                        {[
                            i18n.translate({ id: 'tabs.configs', sourceName: 'OpenAPI' }),
                            i18n.translate({ id: 'testing.title', sourceName: 'OpenAPI' }),
                        ].map((tab, index) => (
                            <div
                                key={index}
                                className={`relative z-[10] flex-1 text-center p-2 px-6 cursor-pointer ${
                                    index === activeTab ? 'text-primary' : 'text-layout-textcolor'
                                }`}
                                onClick={() => setActiveTab(index as 0 | 1)}
                            >
                                {tab}
                            </div>
                        ))}
                    </div>
                </div>
                <div
                    className={`flex-1 min-h-0 gap-6 grid grid-cols-12 relative overflow-y-auto p-5 xl:p-10 xl:!pb-0 pb-10 xl:pt-5 `}
                >
                    <AdminBox
                        classString={`bg-transparent p-0 xl:pb-5 mb-0 shadow-none col-span-12 md:col-span-12 lg:col-span-6 ${
                            isMobile ? (activeTab === 0 ? 'block' : 'hidden') : ''
                        }`}
                    >
                        <div className="flex flex-col gap-6">
                            <AdminBoxHeadline
                                headLine={
                                    isNew
                                        ? i18n.translate({ id: 'form.title.create', sourceName: 'OpenAPI' })
                                        : i18n.translate({ id: 'form.title.edit', sourceName: 'OpenAPI' })
                                }
                                subLine={isNew ? undefined : config?.title}
                            />
                            <div className="flex flex-col gap-6">
                                {/* Basic Information */}
                                <AdminBox classString={`mb-0 relative`}>
                                    <InfoButton configKey="openapiBasicInfo" />
                                    <BasicInformationSection
                                        isNew={isNew}
                                        title={formData.title}
                                        baseUrl={formData.baseUrl}
                                        onTitleChange={(title) => updateFormData({ title })}
                                        onBaseUrlChange={(baseUrl) => updateFormData({ baseUrl })}
                                        errors={errors}
                                    />
                                </AdminBox>

                                {/* Authentication */}
                                <AdminBox classString={`mb-0 relative`}>
                                    <InfoButton configKey="openapiAuth" />
                                    <AuthenticationSection
                                        authType={formData.authType}
                                        authData={formData.authData}
                                        onAuthTypeChange={(authType) => updateFormData({ authType, authData: {} })}
                                        onAuthDataChange={updateAuthData}
                                    />
                                </AdminBox>

                                {/* OpenAPI Specification */}
                                <AdminBox contentLoaded={!isLoading} classString={`mb-0 relative`}>
                                    <InfoButton configKey="openapiSpec" />
                                    <OpenApiSpecSection
                                        openApiSpec={formData.openApiSpec}
                                        onSpecChange={(openApiSpec) => updateFormData({ openApiSpec })}
                                        errors={errors}
                                    />
                                </AdminBox>

                                {/* Form Controls */}
                                <FormControls
                                    isLoading={isLoading}
                                    isNew={isNew}
                                    onSubmit={handleSave}
                                    onCancel={handleCancel}
                                />
                            </div>
                        </div>
                    </AdminBox>
                    <AdminBox
                        classString={`${
                            !isMobile ? `${boxHeightClassesStr} xl:sticky top-0` : ''
                        } !mb-0 col-span-12 md:col-span-12 lg:col-span-6 ${
                            isMobile ? (activeTab === 1 ? 'block' : 'hidden') : 'lg:block'
                        } shadow-none flex flex-col ${!isMobile ? 'overflow-y-auto' : ''}`}
                    >
                        {/* Right column content - individually scrollable */}
                        <div className={`flex-1 min-h-0`}>
                            <OpenApiTester
                                resetSignal={resetFlag}
                                setResetFlag={setResetFlag}
                                preselectedConfigIdentifier={config?.identifier ?? configIdentifier}
                            />
                        </div>
                    </AdminBox>
                </div>
            </div>
        </AdminContainer>
    );
}

export default OpenApiConfigPage;
